import React, { useRef, useState } from "react";
import Button from "@amzn/meridian/esm/components/button";
import ButtonGroup, { ButtonOption } from "@amzn/meridian/esm/components/button-group";
import checkKnockoutTokens from "@amzn/meridian-tokens/base/icon/check-knockout"
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import Column from "@amzn/meridian/esm/components/column";
import Icon from "@amzn/meridian/esm/components/icon";
import Popover from "@amzn/meridian/esm/components/popover";
import RadioButton from "@amzn/meridian/esm/components/radio-button";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Textarea from "@amzn/meridian/esm/components/textarea";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import thumbsDownTokens from "@amzn/meridian-tokens/base/icon/thumbs-down";
import thumbsUpTokens from "@amzn/meridian-tokens/base/icon/thumbs-up";

import LogManager from "../../utils/LogManager";
import aimsTheme from "../../../meridian-theme-aims.json";
import FeedbackPopoverTop from "../../../images/feedback_popover_top.svg";
import feedbackNotSubmitted from "../../../images/feedback_not_submitted.svg";
import feedbackSubmitted from "../../../images/feedback_submitted.svg";
import {
    FEEDBACK_TYPE,
    FEEDBACK, 
    FEEDBACK_MESSAGES,
    FEEDBACK_REASONS, 
    FEEDBACK_THEME_TOKENS
} from '../constants';

/**
 * Feedback component for meeting summaries with thumbs up/down options.
 * Handles user feedback submission and API interaction.
 *
 * @param {string} aimsUuid - UUID for the meeting summary
 * @returns {JSX.Element} Summary feedback UI including ButtonGroup and Popover
 */

const SummaryFeedback = ({ aimsUuid }) => {
    const popoverRef = useRef()
    const [feedback, setFeedback] = useState(undefined);
    const [isFeedbackDisabled, setIsFeedbackDisabled] = useState(false);
    const [feedbackReason, setFeedbackReason] = useState(undefined);
    const [feedbackComment, setFeedbackComment] = useState(undefined);
    const [thumbsDownPopoverOpen, setThumbsDownPopoverOpen] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showSubmittedIcon, setShowSubmittedIcon] = useState(false);

    const onCloseSubmittedPopover = () => {
        setThumbsDownPopoverOpen(false);
        setFeedbackReason(undefined);
        setFeedbackComment(undefined);
        setIsSubmitted(false); // Reset the submitted state when pop over is closed
    };

    const onClosePopover = () => {
        onCloseSubmittedPopover();
        setFeedback(undefined);
        setIsFeedbackDisabled(false);
    }

    const handleFeedback = async (feedback) => {
        if (!isFeedbackDisabled) {
            setIsFeedbackDisabled(true);
            setFeedback(feedback);
            try {
                if (feedback === FEEDBACK.THUMBS_DOWN) {
                    setThumbsDownPopoverOpen(true);
                } else {
                    // For thumbs up, directly submit the feedback
                    await submitFeedback(feedback);
                }
            } catch (error) {
                // If there's an error, re-enable the buttons
                setIsFeedbackDisabled(false);
                setFeedback(undefined);
                LogManager.error('Error handling feedback:', error);
            }
        }

    };

    const submitFeedback = async (feedback) => {
        LogManager.info("Calling /feedback API with aimsUuid: " + aimsUuid);
        try {
            const response = await fetch('/feedback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    type: FEEDBACK_TYPE.SUMMARY,
                    aimsUuid: aimsUuid,
                    feedback: feedback,
                    feedbackTimestamp: Math.floor(Date.now() / 1000),
                    feedbackReason: feedbackReason,
                    feedbackComment: feedbackComment
                })
            });
            const responseJson = await response.json();
            LogManager.info("The response of /feedback is:", responseJson);

            //TODO error handling for statusCode
            const statusCode = responseJson.StatusCode;

            setIsFeedbackDisabled(true);
            setShowSubmittedIcon(true);
        } catch (error) {
            setIsFeedbackDisabled(false);
            setFeedback(undefined);
            LogManager.error('Error submitting feedback:', error);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitted(true);
        await submitFeedback(FEEDBACK.THUMBS_DOWN);
    };

    return (
        <>
            {/* Thumbs up/down button */}
            <Row>
                <Column width="10px" height="70px"/>
                <ButtonGroup 
                    value={feedback} 
                    onChange={handleFeedback} 
                    size="small" 
                    type="outline"
                    minWidth="120px"
                >
                    <Theme tokens={FEEDBACK_THEME_TOKENS}>
                        <ButtonOption 
                            value={FEEDBACK.THUMBS_UP}
                            disabled={isFeedbackDisabled}
                        >
                            <Icon tokens={thumbsUpTokens}/>
                        </ButtonOption>
                    </Theme>
                    <Theme tokens={FEEDBACK_THEME_TOKENS}>
                        <ButtonOption 
                            value={FEEDBACK.THUMBS_DOWN}
                            disabled={isFeedbackDisabled}
                        >
                            <Icon tokens={thumbsDownTokens}/>
                        </ButtonOption>
                    </Theme>
                </ButtonGroup>
                {showSubmittedIcon &&
                    <Icon tokens={checkKnockoutTokens} color="success"/>
                }
            </Row>
            {/* Thumbs down popover */}
            <Column alignmentHorizontal="center">
                <Row ref={popoverRef}/>
                {thumbsDownPopoverOpen &&
                    <Theme palette={aimsTheme}>
                        <Popover
                            open={true}
                            minWidth="292px"
                            anchorNode={popoverRef.current}
                            onClose={()=>{}}
                            position="top"
                            indicatorArrow={false}
                            spacingInset="none"
                        >
                            <Column heights={["20%","65%","15%"]} height="370px" spacing="none">
                                {isSubmitted && feedback === FEEDBACK.THUMBS_DOWN ? 
                                    <>
                                        <div                     
                                            style={{
                                                background: `url(${FeedbackPopoverTop}) no-repeat center/cover`,
                                                width: "100%",
                                            }}
                                        >
                                            <Row widths={["80%","20%"]} alignmentVertical="bottom" height="50px">
                                                <div/>
                                                <Button type="icon" onClick={onCloseSubmittedPopover}>
                                                    <Icon tokens={closeSmallTokens}/>
                                                </Button>
                                            </Row>
                                        </div>
                                        <Column alignmentHorizontal="center" alignmentVertical="center">
                                            {feedbackComment || feedbackReason ? 
                                            <>
                                                <img
                                                    src={feedbackSubmitted}
                                                    style={{width: '100px', height: 'auto', maxWidth: '100%'}}
                                                />
                                                <Text type="h50">
                                                    {FEEDBACK_MESSAGES.SUBMITTED}
                                                </Text>
                                            </>
                                            :
                                            <>
                                                <img
                                                    src={feedbackNotSubmitted}
                                                    style={{width: '60px', height: 'auto', maxWidth: '100%'}}
                                                />
                                                <Text type="h50">
                                                    {FEEDBACK_MESSAGES.NOT_SUBMITTED}
                                                </Text>
                                            </>
                                            }
                                        </Column>
                                        <Row alignmentHorizontal="end" spacingInset="none 500 none none">
                                            <Button type="primary" size="small" minWidth="90px" onClick={onCloseSubmittedPopover}>Close</Button>
                                        </Row>
                                    </>
                                :
                                    <>
                                        <div                     
                                            style={{
                                                background: `url(${FeedbackPopoverTop}) no-repeat center/cover`,
                                                width: "100%",
                                            }}
                                        >
                                            <Row widths={["10%", "70%","20%"]} alignmentVertical="bottom" height="50px">
                                                <div/>
                                                <Text type="h100">
                                                    Share Feedback
                                                </Text>
                                                <Button type="icon" onClick={onClosePopover}>
                                                    <Icon tokens={closeSmallTokens}/>
                                                </Button>
                                            </Row>
                                        </div>
                                        <Column spacingInset="500 400 400 400" spacing="500">
                                            <Row alignmentHorizontal="center">
                                                <Column spacing="300">
                                                    <RadioButton
                                                        checked={feedbackReason === FEEDBACK_REASONS.NOT_ACCURATE}
                                                        onChange={setFeedbackReason}
                                                        name="feedbackReason"
                                                        value={FEEDBACK_REASONS.NOT_ACCURATE}
                                                        size="small"
                                                    >
                                                        {FEEDBACK_REASONS.NOT_ACCURATE}
                                                    </RadioButton>
                                                    <RadioButton
                                                        checked={feedbackReason === FEEDBACK_REASONS.NOT_COMPLETE}
                                                        onChange={setFeedbackReason}
                                                        name="feedbackReason"
                                                        value={FEEDBACK_REASONS.NOT_COMPLETE}
                                                        size="small"
                                                    >
                                                        {FEEDBACK_REASONS.NOT_COMPLETE}
                                                    </RadioButton>
                                                    <RadioButton
                                                        checked={feedbackReason === FEEDBACK_REASONS.BLOCKED}
                                                        onChange={setFeedbackReason}
                                                        name="feedbackReason"
                                                        value={FEEDBACK_REASONS.BLOCKED}
                                                        size="small"
                                                    >
                                                        {FEEDBACK_REASONS.BLOCKED}
                                                    </RadioButton>
                                                </Column>
                                            </Row>
                                            <Row alignmentHorizontal="center">
                                                <Textarea
                                                    value={feedbackComment}
                                                    onChange={setFeedbackComment}
                                                    placeholder="Tell us more"
                                                    error={false}
                                                    width="230px"
                                                    rows={3}
                                                    size="small"
                                                />
                                            </Row>
                                        </Column>
                                        <Row alignmentHorizontal="end" spacingInset="none 500 none none">
                                            <Button type="primary" size="small" minWidth="90px" onClick={handleSubmit}>
                                                Submit
                                            </Button>
                                        </Row>
                                    </>
                                }
                            </Column>
                        </Popover>
                    </Theme>
                }
            </Column>
        </>
    );
};

export default SummaryFeedback;